import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZapIcon, TargetIcon, CalendarCheckIcon } from 'lucide-react';
import { SlideFrame, MetricChip } from '../SlideFrame';
import { TalabWindow, TalabAskBar, TalabUserBubble, TalabAgentBubble, TalabTypingRow } from '../TalabWindow';
import { TalabResultCard } from '../TalabResultCard';
import { DeckDetailModal } from '../DeckDetailModal';
import { Cursor } from '../Cursor';
import { CAR_RESULTS } from '../../../data/deck';
import { useTypewriter } from '../../../hooks/useTypewriter';
import { useSequence } from '../../../hooks/useSequence';
const EASE = [0.23, 1, 0.32, 1] as const;
const PROMPT = 'I want to rent a car for a budget of AED 100 per day for 7 days starting tomorrow.';
const UNDERSTOOD = [{
  icon: TargetIcon,
  label: 'Budget',
  value: 'AED 100 / day'
}, {
  icon: CalendarCheckIcon,
  label: 'Dates',
  value: 'Tomorrow · 7 days'
}, {
  icon: ZapIcon,
  label: 'Intent',
  value: 'Rent, not buy'
}];
export function SlideTalabAsk({
  active
}: {
  active: boolean;
}) {
  const {
    typed,
    done
  } = useTypewriter(PROMPT, {
    active,
    startDelay: 600,
    speed: 34
  });
  // 1 thinking · 2 answer · 3 results · 4 open details · 5–6 browse photos
  // 7 read further down · 8 close and return to the results
  const step = useSequence([4400, 1000, 350, 1500, 2000, 2000, 2000, 2100], active);
  const detailOpen = step >= 4 && step < 8;
  const imageIndex = step >= 6 ? 2 : step >= 5 ? 1 : 0;
  const cursor = step >= 8 ? {
    x: 796,
    y: 118
  } : step >= 7 ? {
    x: 650,
    y: 300
  } : step >= 5 ? {
    x: 440,
    y: 280
  } : step >= 4 ? {
    x: 197,
    y: 383
  } : {
    x: 250,
    y: 300
  };
  return <SlideFrame eyebrow="The Talab experience" title="With Talab, the customer simply asks" data-id="element-2296" data-mp="34f6fdxh1ts4s">
      <div className="h-full flex gap-8" data-id="element-2297" data-mp="2zdquc5evflme">
        <div className="w-[286px] shrink-0 flex flex-col" data-id="element-2298" data-mp="2zgv4rkixjdli">
          <p className="text-[13.5px] leading-relaxed text-slate-500" data-id="element-2299" data-mp="2veqd2rc1kr1i">
            Talab understands intent, budget, dates, and preferences — and turns a request into
            relevant results instantly.
          </p>

          <div className="mt-6 space-y-2.5" data-id="element-2300" data-mp="2ppbryjpwmj49">
            {UNDERSTOOD.map((item, index) => <motion.div key={item.label} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white border border-slate-100 shadow-soft" initial={{
            opacity: 0,
            x: -12
          }} animate={step >= 1 ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0.25,
            x: -12
          }} transition={{
            duration: 0.4,
            ease: EASE,
            delay: index * 0.1
          }} data-id="element-2301" data-mp="2bthserb0t63t" data-mpi={index}>
                <item.icon className="w-4 h-4 text-primary-indigo shrink-0" data-id="element-2302" data-mp="2zp2lai2qas2r" />
                <div className="min-w-0" data-id="element-2303" data-mp="3nf6st0j1fbz1">
                  <p className="text-[9px] uppercase tracking-[0.16em] text-slate-400" data-id="element-2304" data-mp="3ne5cagj95x9x">
                    {item.label}
                  </p>
                  <p className="text-[12px] font-semibold text-dark-navy truncate" data-id="element-2305" data-mp="3s7q57u5zo129">
                    {item.value}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <motion.div className="mt-auto flex flex-wrap gap-2" initial={{
          opacity: 0,
          y: 10
        }} animate={step >= 3 ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 10
        }} transition={{
          duration: 0.45,
          ease: EASE
        }} data-id="element-2306" data-mp="f4upqe80ki78">
            <MetricChip label="Request" value="1" tone="positive" data-id="element-2307" data-mp="3im4g0ggpxvq4" />
            <MetricChip label="To answer" value="8 sec" tone="positive" data-id="element-2308" data-mp="3elautuunuv0x" />
          </motion.div>
        </div>

        <div className="relative flex-1 min-w-0" data-id="element-2309" data-mp="3skttlx1l0u0k">
          <TalabWindow url="talab.ai/easylease" storeName="EasyLease Rentals" className="absolute inset-0" data-id="element-2310" data-mp="3nl7d9eqgixnj">
            <div className="flex flex-col h-full p-4" data-id="element-2311" data-mp="17s8evq5h09gs">
            <div className="flex-1 min-h-0 flex flex-col justify-end gap-3.5 overflow-hidden" data-id="element-2312" data-mp="12v879asz2ynk">
              {done && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                ease: EASE
              }} data-id="element-2313" data-mp="38k0sk23c7njy">
                  <TalabUserBubble data-id="element-2314" data-mp="3igdn126pi5fr">{PROMPT}</TalabUserBubble>
                </motion.div>}

              <AnimatePresence mode="wait" data-id="element-2315" data-mp="3mkbldao7jc7r">
                {step === 1 && <TalabTypingRow key="typing" data-id="element-2316" data-mp="3rid41uhq19ip" />}
                {step >= 2 && <motion.div key="answer" className="space-y-3" data-id="element-2317" data-mp="bk2gpj0n8dn">
                    <TalabAgentBubble data-id="element-2318" data-mp="9pibdfkjkam">
                      Found <span className="font-semibold text-dark-navy" data-id="element-2319" data-mp="52iirp4086gw">3 cars</span> within
                      AED 100/day, available from tomorrow for 7 days — with the full 7-day total
                      already calculated.
                    </TalabAgentBubble>
                    {step >= 3 && <div className="flex gap-3 pl-[40px]" data-id="element-2320" data-mp="j1ic3a2gp9je">
                        {CAR_RESULTS.map((result, index) => <TalabResultCard key={result.id} result={result} delay={index * 0.09} data-id="element-2321" data-mp="23kvjt3xt4pxu" data-mpi={index} />)}
                      </div>}
                  </motion.div>}
              </AnimatePresence>
            </div>

              <div className="shrink-0 pt-3.5" data-id="element-2322" data-mp="2matpjde4u6yb">
                <TalabAskBar compact text={done ? '' : typed} caret={!done} focused={!done} data-id="element-2323" data-mp="2mcob8tx8a33g" />
              </div>
            </div>

            <DeckDetailModal result={CAR_RESULTS[0]} open={detailOpen} imageIndex={imageIndex} scrolled={step >= 7} data-id="element-2324" data-mp="2di09veurx5jx" />
          </TalabWindow>

          <Cursor x={cursor.x} y={cursor.y} clickId={step === 7 ? 6 : step} visible={step >= 3} data-id="element-2325" data-mp="35z2vqrcigdgt" />
        </div>
      </div>
    </SlideFrame>;
}